import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { PendingService } from '@mydocma/app-container';
import { AuthService, ClaimTypes, JwtAuthService, User } from '@mydocma/auth';
import {
  SystemPagesService,
  SystemPageType,
} from '@mydocma/component-library/src/lib/system-pages';
import { ConfigProvider } from '@mydocma/core';
import { TranslateService } from '@ngx-translate/core';
import { ProjectService } from '@mydocma/project';
import { catchError, filter, from, map, Observable, of, switchMap, take } from 'rxjs';
import { PR_CONFIG, PrConfig } from '../configuration/pr.config';
import { DocumentApiService } from '../services/document-api.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentViewGuard {
  private jwtAuthService: JwtAuthService;

  constructor(
    @Inject(PR_CONFIG) private readonly config: PrConfig,
    private readonly authService: AuthService,
    private readonly projectService: ProjectService,
    private readonly documentApiService: DocumentApiService,
    private readonly systemPagesService: SystemPagesService,
    private readonly pendingService: PendingService,
    private readonly translateService: TranslateService,
    private readonly configProvider: ConfigProvider<PrConfig>,
  ) {
    this.jwtAuthService = this.authService as any;
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const projectId = route.paramMap.get('projectId');
    const documentId = route.paramMap.get('documentId');

    if (!projectId || !documentId) {
      this.handleNoDocumentFound(state);
      return of(false);
    }

    return this.jwtAuthService.loadedState.pipe(
      filter((loaded: boolean) => loaded),
      take(1),
      switchMap(() => from(this.configProvider.loadConfigAsync())),
      switchMap((config: PrConfig) =>
        this.jwtAuthService.user.pipe(
          take(1),
          switchMap((user: User) => {
            if (!user) {
              this.jwtAuthService.startSignin(state.url);
              return of(false);
            }

            return this.projectService.getProjectById(projectId).pipe(
              switchMap((project) =>
                from(
                  this.jwtAuthService.refreshToken({
                    [ClaimTypes.customer_id]: project?.customerId,
                    [ClaimTypes.project_id]: project?.id,
                    [ClaimTypes.app_Id]: config?.mydocmaConfig?.appId,
                  }),
                ).pipe(
                  switchMap(() => this.projectService.setCurrentProject(project)),
                  switchMap(() =>
                    this.documentApiService.getDocumentMetadataById(documentId, false),
                  ),
                  map((document) => {
                    const claims = this.jwtAuthService.getAccessTokenClaims();

                    if (!claims?.app_id || claims?.project !== projectId) {
                      this.handleUnauthorizedAccess(state);
                      return false;
                    }

                    if (!document?.id) {
                      this.handleNoDocumentFound(state);
                      return false;
                    }

                    return true;
                  }),
                ),
              ),
            );
          }),
        ),
      ),
      catchError((error) => {
        if (error?.status === 403) {
          this.handleUnauthorizedAccess(state);
          return of(false);
        }

        if (error?.status === 404) {
          this.handleNoDocumentFound(state);
          return of(false);
        }

        this.handleUnauthorizedAccess(state);
        return of(false);
      }),
    );
  }

  private handleUnauthorizedAccess(state?: RouterStateSnapshot): void {
    const fullUrl = window.location.origin + state?.url;

    this.pendingService.closeInitLoader();
    this.systemPagesService.setPage({
      type: SystemPageType.NoAccess,
      urlOnRetry: state ? fullUrl : undefined,
      content: {
        title: this.translateService.instant('PR.systemPages.noAccess.title'),
        description:
          this.translateService.instant('PR.systemPages.noAccess.description') +
          '\n' +
          this.translateService.instant('PR.systemPages.noAccess.contactSupport'),
      },
      actions: [
        {
          label: this.translateService.instant('PR.buttons.logout'),
          clicked: () => {
            this.jwtAuthService.signout(undefined, fullUrl);
          },
        },
      ],
    });
    this.systemPagesService.openNoAccessPage();
  }

  private handleNoDocumentFound(state?: RouterStateSnapshot): void {
    const fullUrl = window.location.origin + state?.url;

    this.pendingService.closeInitLoader();
    this.systemPagesService.setPage({
      type: SystemPageType.NotFound,
      urlOnRetry: state ? fullUrl : undefined,
      content: {
        description: this.translateService.instant('PR.systemPages.docsNotFound.description'),
      },
    });
    this.systemPagesService.openNotFoundPage();
  }
}